import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/authService';

export const useAdminDataProtection = (initialFilters = {}) => {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState({
    record_type: '',
    status: '',
    compliance_status: '',
    data_category: '',
    assigned_to: '',
    date_from: '',
    date_to: '',
    ...initialFilters
  });

  const statusOptions = [
    { value: '', label: 'All Statuses' },
    { value: 'active', label: 'Active' },
    { value: 'archived', label: 'Archived' },
    { value: 'deleted', label: 'Deleted' }
  ];

  const complianceStatusOptions = [
    { value: '', label: 'All Compliance Statuses' },
    { value: 'compliant', label: 'Compliant' },
    { value: 'non_compliant', label: 'Non-Compliant' },
    { value: 'under_review', label: 'Under Review' },
    { value: 'not_applicable', label: 'Not Applicable' }
  ];

  const cleanFilters = useMemo(() => {
    return Object.fromEntries(
      Object.entries(filters).filter(([_, value]) => value !== '' && value !== null && value !== undefined)
    );
  }, [filters]);

  const queryString = new URLSearchParams(cleanFilters).toString();

  const { data: recordsResponse, isLoading, error } = useQuery({
    queryKey: ['adminDataProtection', cleanFilters],
    queryFn: async () => {
      const response = await api.get(`/admin/data-protection${queryString ? `?${queryString}` : ''}`);
      return response.data;
    }
  });

  // Fetch statistics
  const { data: statsResponse, isLoading: isLoadingStats } = useQuery({
    queryKey: ['adminDataProtectionStats'],
    queryFn: async () => {
      const response = await api.get('/admin/data-protection/statistics');
      return response.data;
    }
  });

  const createMutation = useMutation({
    mutationFn: async (data) => {
      const response = await api.post('/admin/data-protection', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['adminDataProtection']);
      queryClient.invalidateQueries(['adminDataProtectionStats']);
    }
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await api.put(`/admin/data-protection/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['adminDataProtection']);
      queryClient.invalidateQueries(['adminDataProtectionStats']);
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      const response = await api.delete(`/admin/data-protection/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['adminDataProtection']);
      queryClient.invalidateQueries(['adminDataProtectionStats']);
    }
  });

  const records = useMemo(() => {
    const data = recordsResponse?.data?.data || recordsResponse?.data;
    if (data && Array.isArray(data)) {
      return data;
    }
    if (data && data.data && Array.isArray(data.data)) {
      return data.data;
    }
    return [];
  }, [recordsResponse]);

  const statistics = useMemo(() => {
    return statsResponse?.data || {};
  }, [statsResponse]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSearch = (data, searchTerm) => {
    if (!searchTerm) return data;
    
    return data.filter(record => 
      record.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      record.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      record.record_type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      record.data_category?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const getComplianceStatusColor = (status) => {
    const statusConfig = {
      compliant: 'bg-green-100 text-green-800',
      non_compliant: 'bg-red-100 text-red-800',
      under_review: 'bg-yellow-100 text-yellow-800',
      not_applicable: 'bg-gray-100 text-gray-800'
    };
    return statusConfig[status] || 'bg-gray-100 text-gray-800';
  };

  return {
    records,
    isLoading: isLoading || isLoadingStats,
    error,
    filters,
    statusOptions,
    complianceStatusOptions,
    handleFilterChange,
    handleSearch,
    statistics,
    getComplianceStatusColor,
    createRecord: createMutation.mutate,
    isCreating: createMutation.isPending,
    updateRecord: updateMutation.mutate,
    isUpdating: updateMutation.isPending,
    deleteRecord: deleteMutation.mutate,
    isDeleting: deleteMutation.isPending
  };
};


